/* eslint-disable no-param-reassign */
/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable no-nested-ternary */

import { SendOutlined } from "@ant-design/icons";
import { AccelFile } from "@axl-dev/school-sdk";
import { Button, Tooltip } from "antd";
import cn from "classnames";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useLoc } from "@app/shared/lib/hooks";
import { useClipboardFiles } from "@app/shared/lib/hooks/useClipboardFiles";
import { useLatestValueRef } from "@app/shared/lib/hooks/useLatestValueRef";
import { isEmpty } from "@app/shared/lib/is-empty";
import { reorderArray } from "@app/shared/lib/reorder-array";
import { trimText } from "@app/shared/lib/trim-text";

import { Files } from "../Files/Files";
import { LinkifyText } from "../LinkifyText";
import { UserAvatar } from "../UserAvatar";

import { CommentFormHandler, CommentFormProps } from "./CommentForm";
import { Editor, EditorHandler } from "./components/Editor/Editor";
import { EmojiInput } from "./components/EmojiInput/EmojiInput";
import { FileInput } from "./components/FileInput/FileInput";
import { VoiceInput } from "./components/VoiceInput/VoiceInput";
import { VoiceItem, VoiceList } from "./components/VoiceList/VoiceList";

import styles from "./Comment.module.scss";

/**
 * Comment editor form that supports text/emoji/files, voice recording, etc.
 */
export const CommentFormEditor: React.FC<CommentFormProps> = ({
  enterToSubmit = true,
  useFiles = true,
  fileProps = {
    view: "list",
    draggable: false,
  },
  useEmoji = true,
  useVoice = false,
  autoSize = { minRows: 2, maxRows: 5 },
  voiceMode = "split",
  editing = true,
  ...props
}) => {
  const loc = useLoc();
  const editorRef = useRef<EditorHandler>(null);
  const voiceInputHandler = VoiceInput.useHandler();
  const [text, _setText] = useState(props.text ?? props.defaultText ?? "");
  const setText = useCallback(
    (nextText: string, force = false) => {
      if (!props.text || force) {
        _setText(nextText);
        props.onTextChange?.(nextText);
      }
    },
    [props.text, props.onTextChange],
  );

  const [files, _setFiles] = useState(() =>
    (props.files ?? props.defaultFiles ?? []).slice(),
  );
  const filesRef = useLatestValueRef(files);
  const setFiles = useCallback(
    (nextFiles: AccelFile[], force = false) => {
      if (!props.files || force) {
        _setFiles(nextFiles.slice());
      }
    },
    [props.files],
  );
  const [voices, setVoices] = useState(
    () => (props.voices ?? props.defaultVoices ?? []).slice() as AccelFile[],
  );
  const voicesRef = useLatestValueRef(voices);
  const addVoice = useCallback(
    (file: AccelFile) => {
      setVoices((prev) => [...prev, file]);
      props.onVoicesChange?.([...voicesRef.current, file], {
        added: [file],
      });
    },
    [props.onVoicesChange],
  );
  const removeVoice = useCallback(
    (file: AccelFile) => {
      setVoices((prevVoices) =>
        prevVoices.filter((x) => x.id !== file.id || x !== file),
      );
      props.onVoicesChange?.(
        voicesRef.current.filter((x) => x.id !== file.id || x !== file),
        {
          deleted: [file],
        },
      );
    },
    [props, voicesRef],
  );
  const clearVoices = useCallback(() => {
    const prev = voicesRef.current.slice();
    setVoices([]);
    props.onVoicesChange?.([], {
      deleted: prev,
    });
  }, [props, voicesRef]);
  const trimmedText = useMemo(() => {
    return text ? trimText(text) : text;
  }, [text]);
  const [sending, setSending] = useState(false);
  const [_disabled, setDisabled] = useState(props.disabled ?? false);
  const isMaxLengthReached = useMemo(() => {
    if (!props.maxLength || !text) return false;

    return trimmedText.length > props.maxLength;
  }, [props.maxLength, trimmedText, text]);
  const hasText = useMemo(() => {
    return !isEmpty(trimmedText);
  }, [trimmedText]);

  const hasAttachments = useMemo(() => {
    return files.length > 0;
  }, [files]);

  const hasTextOrAttachments = useMemo(() => {
    return hasText || hasAttachments;
  }, [hasText, hasAttachments]);

  const canSend = useMemo(() => {
    if (isMaxLengthReached) return false;

    return hasText || hasAttachments || voices.length > 0;
  }, [hasAttachments, hasText, isMaxLengthReached, voices.length]);
  const availableFileCount = useMemo(() => {
    return fileProps?.maxFiles
      ? Math.max(0, fileProps.maxFiles - files.length)
      : null;
  }, [fileProps?.maxFiles, files.length]);
  const canAttachFiles = useMemo(() => {
    return availableFileCount == null || availableFileCount > 0;
  }, [availableFileCount]);
  const disabled = useMemo(() => {
    return _disabled || sending;
  }, [_disabled, sending]);
  const [hasFocus, setFocus] = useState(false);
  const [sendHandler, setSendHandler] = useState(() => props.onSend);
  const send = async () => {
    if (!canSend || !sendHandler) return false;
    setSending(true);
    let filesToSend = files.slice();
    if (voices.length > 0) filesToSend = [...voices, ...filesToSend];
    const success = await sendHandler(trimmedText, filesToSend);
    if (success) {
      if (props.clearAfterSending === true) {
        setFiles([]);
        clearVoices();
        setText("");
      }
    }

    setSending(false);

    return success;
  };

  const attachFiles = useCallback(
    (attachedFiles: AccelFile[]) => {
      if (attachedFiles.length === 0) return;
      if (availableFileCount != null) {
        if (availableFileCount === 0) return;
        // take max images we can upload
        if (attachedFiles.length > availableFileCount)
          attachedFiles = attachedFiles.slice(0, availableFileCount);
      }

      _setFiles((prevFiles) => [...prevFiles, ...attachedFiles]);

      props.onFilesChange?.([...filesRef.current, ...attachedFiles], {
        added: attachedFiles,
      });
    },
    [availableFileCount, filesRef, props],
  );
  const deleteFile = useCallback(
    (file: AccelFile) => {
      _setFiles((prevFiles) =>
        prevFiles.filter((x) => x.id !== file.id || x !== file),
      );
      props.onFilesChange?.(
        filesRef.current.filter((x) => x.id !== file.id || x !== file),
        {
          deleted: [file],
        },
      );
    },
    [filesRef, props],
  );
  const reorderFiles = useCallback(
    (src: number, dst: number) => {
      _setFiles((prevFiles) => reorderArray(prevFiles, src, dst).items);
      props.onFilesChange?.(reorderArray(filesRef.current, src, dst).items, {});
    },
    [filesRef, props],
  );

  const handler = useMemo<CommentFormHandler>(
    () => ({
      focus: () => editorRef.current?.focus(),
      blur: () => editorRef.current?.blur(),
      setText: (nextText: string) => {
        setText(nextText, true);
      },
      insertText: (nextText: string) => {
        editorRef.current?.insert(nextText);
      },
      setFiles: (nextFiles: AccelFile[]) => {
        setFiles(nextFiles, true);
      },
      setVoices: (nextVoices: AccelFile[]) => {
        setVoices(nextVoices);
      },
      hasUploadingFiles: () => {
        return filesRef.current.some((x) => !x.isUploaded);
      },
      cancelVoiceRecording: () => {
        voiceInputHandler.current?.cancelRecording();
      },
    }),
    [filesRef, setFiles, setText, voiceInputHandler],
  );

  useEffect(() => {
    if (props.handlerRef) props.handlerRef.current = handler;
  }, [handler, props.handlerRef]);

  useClipboardFiles(
    (file) => {
      if (!hasFocus) return;
      attachFiles([AccelFile.fromFile(file)]);
    },
    [useFiles],
    useFiles === true,
  );

  useEffect(() => {
    setSendHandler(() => props.onSend);
  }, [props.onSend]);
  useEffect(() => {
    if (props.text === undefined) return;
    setText(props.text, true);
  }, [props.text, setText]);

  useEffect(() => {
    if (props.defaultText === undefined) return;
    setText(props.defaultText);
  }, [props.defaultText, setText]);

  useEffect(() => {
    if (props.defaultFiles === undefined) return;
    setFiles(props.defaultFiles);
  }, [props.defaultFiles, setFiles]);

  useEffect(() => {
    if (props.defaultVoices === undefined) return;
    setVoices(props.defaultVoices);
  }, [props.defaultVoices]);

  useEffect(() => {
    if (props.files === undefined) return;
    setFiles(props.files);
  }, [props.files, setFiles]);

  useEffect(() => {
    if (props.disabled !== undefined) setDisabled(props.disabled);
  }, [props.disabled]);

  useEffect(() => {
    if (props.voices === undefined) return;
    setVoices(props.voices);
  }, [props.voices]);

  const handleRecordingEnded = useCallback(
    (file: AccelFile) => {
      addVoice(file);
      props.onVoiceRecordingEnded?.(file);
    },
    [props.onVoiceRecordingEnded, addVoice],
  );

  const handleRecordingCanceled = useCallback(() => {
    props.onVoiceRecordingCanceled?.();
  }, [props.onVoiceRecordingCanceled]);

  const sendButton =
    props.sendDisabled === true ? null : (
      <Tooltip
        title={loc.word("Max text length reached", {
          default: "Max text length reached",
        })}
        open={isMaxLengthReached}
      >
        <Button
          type="link"
          onClick={send}
          disabled={disabled || !canSend}
          size="large"
          icon={<SendOutlined className={props.iconClassname} />}
          className="comment-msg-btn-send"
        />
      </Tooltip>
    );

  const voiceInput = (
    <VoiceInput
      handler={voiceInputHandler}
      deviceId={props.micDeviceId}
      disabled={disabled}
      iconClassname={props.iconClassname}
      onRecordingStarted={props.onVoiceRecordingStarted}
      onRecordingEnded={handleRecordingEnded}
      onRecordingCanceled={handleRecordingCanceled}
    />
  );

  return (
    <div
      className={cn(styles.comment_editor_wrapper, "flex flex-col")}
      style={props.style}
    >
      <div className="flex flex-1 w-100">
        {props.author && (
          <UserAvatar user={props.author} size={40} className="mr-10" />
        )}

        <div
          className="flex flex-1 align-center p-2"
          data-borderless={props.borderless ?? false}
        >
          {voices.length > 0 && voiceMode !== "combined" ? (
            <VoiceItem
              voice={voices[0]!}
              audioSize={props.audioPlayerSize}
              deletable={editing === true}
              downloadable={false}
              onDelete={() => voices[0] && removeVoice(voices[0])}
              suffix={editing === true ? sendButton : null}
              playerStyle={props.audioPlayerStyle}
            />
          ) : editing === true ? (
            <div
              className={cn(
                props.editorClassName,
                styles.comment_editor,
                "flex flex-1 align-center relative",
              )}
              style={props.editorStyle}
            >
              {useFiles && (
                <FileInput
                  disabled={disabled || !canAttachFiles}
                  maxFiles={fileProps?.maxFiles}
                  forbiddenFileExtensions={fileProps?.forbiddenExtensions}
                  maxFileSize={fileProps?.maxFileSize}
                  onAttach={(x) => attachFiles(x)}
                  iconClassname={props.iconClassname}
                />
              )}

              <Editor
                handlerRef={editorRef}
                enterToSubmit={enterToSubmit!}
                text={text}
                autoSize={autoSize}
                disabled={disabled}
                placeholder={props.placeholder}
                autoFocus={props.autoFocus}
                onSend={send}
                onChange={(x) => setText(x)}
                className={props.inputClassName}
                onFocus={() => setFocus(true)}
                onBlur={() => setFocus(false)}
              />
              <div className="flex align-center">
                {props.actionAddons?.(handler)}

                {useEmoji && (
                  <EmojiInput
                    onSelect={(emoji) => editorRef.current?.insert(emoji)}
                    disabled={disabled}
                    iconClassname={props.iconClassname}
                  />
                )}

                {useVoice === true ? (
                  <>
                    {voiceMode === "split" ? (
                      hasTextOrAttachments ? (
                        sendButton
                      ) : (
                        voiceInput
                      )
                    ) : (
                      <>
                        {voiceInput}
                        {sendButton}
                      </>
                    )}
                  </>
                ) : (
                  sendButton
                )}
              </div>
            </div>
          ) : (
            <div>
              {props.textRenderer?.(text) ?? <LinkifyText text={text} />}
            </div>
          )}
        </div>
      </div>
      {voices && voiceMode === "combined" && (
        <div className="mt-10 pl-11 pr-11 overflow-auto w-100">
          <VoiceList
            voices={voices}
            audioSize={props.audioPlayerSize}
            deletable={!disabled && editing === true}
            onDelete={(x) => removeVoice(x)}
            playerStyle={props.audioPlayerStyle}
          />
        </div>
      )}

      {files.length > 0 && (
        <div
          className="mt-10 pl-11 pr-11 overflow-auto w-100"
          style={{ maxHeight: 250 }}
        >
          <Files
            editable={!disabled && editing === true}
            deletable={!disabled && editing === true}
            files={files}
            onDrop={(src, dst) => reorderFiles(src, dst)}
            beforeRemove={async (x) => {
              deleteFile(x);

              // prevent remove
              return false;
            }}
            draggable={fileProps?.draggable}
            view={fileProps?.view || "grid"}
            size={fileProps?.size || "md"}
          />
        </div>
      )}
    </div>
  );
};
